import React from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material'
import AlarmOutlined from '@mui/icons-material/AlarmOutlined'
import LinkOutlined from '@mui/icons-material/LinkOutlined'

import Txt from './Txt'
import Dialog from './Dialog'
import IconButton from './IconButton'
import RestaurantCommunicationLog, { formatDateTime } from './RestaurantCommunicationLog'
import RestaurantAdditionalData from './RestaurantAdditionalData'
import LieferandoLinkButton, { getLieferandoRestaurantUrl } from './LieferandoLinkButton'
import { renderRestaurantMessageDialog } from './RestaurantMessageDialog'
import setRestaurantRelevanceScore from '../mutations/setRestaurantRelevanceScore'
import createRestaurantFromListing from '../mutations/createRestaurantFromListing'
import { handleMutationError } from '../utils/errors'
import { useView } from '../utils/viewContext'
import { useCurrentUser } from '../utils/user'
import { RESTAURANTS_PATH } from '../routes'
import {
	RestaurantCommunication,
	Settlement,
	useTours,
	useRestaurantCommunications,
} from '../utils/settlement'

// Ein Ort, in dem das Restaurant per Sammelbestellung zusätzlich Haushalte erreicht.
export type RestaurantSettlementPotential = {
	settlementId: string
	settlementName: string
	minOrderValue: number | null
	singleOrderMarginShare: number | null
	monthlyRevenue: number
	monthlyGroupOrders: number
	monthlyPlatformRevenue: number
}

export type RankedRestaurant = {
	slug: string
	name: string
	addressLine: string | null
	addressZipCode: string | null
	addressCity: string | null
	cuisines: string[] | null
	phone: string | null
	email: string | null
	relevanceScore: number | null
	isChain: boolean
	rating: number | null
	ratingCount: number | null
	lat: number | null
	lon: number | null
	monthlyRevenue: number
	monthlyPlatformRevenue: number
	// Deckungsbeitrag einer Einzelfahrt zum MBW, gewichtet nach dem Umsatz je Ort; null ohne Fahrzeiten.
	singleOrderMarginShare: number | null
	// Nur wenn die Einzelfahrt in jeden Ort mit bekannter Fahrzeit Verlust bringt.
	unprofitableEverywhere: boolean
	settlements: RestaurantSettlementPotential[]
}

// Bis zu diesem leicht negativen Deckungsbeitrag zählt ein Restaurant im Tooltip noch als relevant.
const MIN_RELEVANT_MARGIN_SHARE = -0.1

const isUnprofitable = (marginShare: number | null): boolean =>
	marginShare !== null && marginShare < 0

const sxChainIcon = { fontSize: '1rem', color: 'text.secondary', flexShrink: 0 } as const

// Markiert Ketten und Franchises, an deren Entscheider man schwer rankommt.
export const ChainIcon = React.memo(
	(): JSX.Element => <LinkOutlined sx={sxChainIcon} titleAccess="Kette" />
)
ChainIcon.displayName = 'ChainIcon'

// Fasst jedes Restaurant über alle Orte zusammen, in die es liefert, und sortiert nach
// Deckungsbeitrag × Umsatz, den es mit Mitbestellt zusätzlich machen würde. Ohne
// Single-Haushalte fällt deren Anteil heraus.
export const getRankedRestaurants = (
	settlements: Settlement[],
	excludeSingles: boolean
): RankedRestaurant[] =>
	Object.values(
		settlements
			.flatMap((settlement) =>
				(settlement.restaurants ?? []).map((provider) => {
					const sizes = provider.potentialBySize.filter(
						({ persons }) => !excludeSingles || persons !== 1
					)
					return {
						provider,
						potential: {
							settlementId: settlement.id,
							settlementName: settlement.name,
							minOrderValue: provider.minOrderValue,
							singleOrderMarginShare: provider.singleOrderMarginShare,
							monthlyRevenue: sizes.reduce((sum, size) => sum + size.monthlyRevenue, 0),
							monthlyGroupOrders: sizes.reduce((sum, size) => sum + size.monthlyGroupOrders, 0),
							monthlyPlatformRevenue: sizes.reduce(
								(sum, size) => sum + size.monthlyPlatformRevenue,
								0
							),
						},
					}
				})
			)
			.filter(({ potential }) => potential.monthlyRevenue > 0)
			.reduce<Record<string, RankedRestaurant>>((bySlug, { provider, potential }) => {
				const restaurant = bySlug[provider.slug]
				return {
					...bySlug,
					[provider.slug]: {
						slug: provider.slug,
						name: provider.name,
						addressLine: provider.addressLine,
						addressZipCode: provider.addressZipCode,
						addressCity: provider.addressCity,
						cuisines: provider.cuisines,
						phone: provider.phone,
						email: provider.email,
						relevanceScore: provider.relevanceScore,
						isChain: provider.isChain,
						rating: provider.rating,
						ratingCount: provider.ratingCount,
						lat: provider.lat,
						lon: provider.lon,
						monthlyRevenue: (restaurant?.monthlyRevenue ?? 0) + potential.monthlyRevenue,
						monthlyPlatformRevenue:
							(restaurant?.monthlyPlatformRevenue ?? 0) + potential.monthlyPlatformRevenue,
						singleOrderMarginShare: null,
						unprofitableEverywhere: false,
						settlements: [...(restaurant?.settlements ?? []), potential].sort(
							(a, b) => b.monthlyRevenue - a.monthlyRevenue
						),
					},
				}
			}, {})
	)
		.map((restaurant) => {
			const withMargin = restaurant.settlements.filter(
				({ singleOrderMarginShare }) => singleOrderMarginShare !== null
			)
			const revenue = withMargin.reduce((sum, { monthlyRevenue }) => sum + monthlyRevenue, 0)
			return {
				...restaurant,
				singleOrderMarginShare: revenue
					? withMargin.reduce(
							(sum, { monthlyRevenue, singleOrderMarginShare }) =>
								sum + monthlyRevenue * (singleOrderMarginShare as number),
							0
					  ) / revenue
					: null,
				unprofitableEverywhere:
					withMargin.length > 0 &&
					withMargin.every(({ singleOrderMarginShare }) => isUnprofitable(singleOrderMarginShare)),
			}
		})
		// Rangfolge: erst die Relevanz (ohne zählt sie als 0), dann Deckungsbeitrag × Umsatzpotenzial. Ohne
		// Fahrzeiten zählt der Deckungsbeitrag als 0, unwirtschaftliche landen mit negativem Wert am Ende.
		.sort(
			(a, b) =>
				(b.relevanceScore ?? 0) - (a.relevanceScore ?? 0) ||
				(b.singleOrderMarginShare ?? 0) * b.monthlyRevenue -
					(a.singleOrderMarginShare ?? 0) * a.monthlyRevenue ||
				b.monthlyRevenue - a.monthlyRevenue
		)

const formatMargin = (marginShare: number | null): string =>
	marginShare === null ? '—' : `${Math.round(marginShare * 100)} %`

const MARGIN_TITLE =
	'Deckungsbeitrag einer einzelnen, selbst ausgefahrenen Lieferando-Bestellung zum MBW, wie die ' +
	'Spalte „Heute“ der Tourrechnung'

const formatEuro = (value: number): string => `${Math.round(value).toLocaleString('de-DE')} €`

const sxRow = {
	'display': 'flex',
	'alignItems': 'center',
	'gap': '0.5rem',
	'p': '0.25rem 0.5rem',
	'borderRadius': '0.25rem',
	'cursor': 'pointer',
	'&:hover': { bgcolor: 'grey.100' },
} as const

const sxRowSelected = {
	...sxRow,
	'bgcolor': 'grey.200',
	'&:hover': { bgcolor: 'grey.200' },
} as const

// Ausgegraut: negative Relevanz, oder eine einzelne Lieferando-Bestellung zum MBW bringt Verlust.
const sxRowUnprofitable = { ...sxRow, opacity: 0.4 } as const
const sxRowSelectedUnprofitable = { ...sxRowSelected, opacity: 0.4 } as const

const sxHeaderRow = { display: 'flex', alignItems: 'center', gap: '0.5rem', p: '0 0.5rem' } as const

// Gepunktet unterstrichen mit Hilfe-Cursor, damit man sieht, dass beim Hovern ein Tooltip kommt.
const sxTooltipHeader = { textDecoration: 'underline dotted', cursor: 'help' } as const

const slotPropsTooltip = {
	tooltip: { sx: { whiteSpace: 'pre-line', maxWidth: '20rem' } },
} as const

const REVENUE_COLUMN_WIDTH = '4.5rem'
const MARGIN_COLUMN_WIDTH = '2.5rem'

const sxRowAlarmIcon = { fontSize: '1rem', color: 'warning.main', flexShrink: 0 } as const

const formatScore = (score: number): string => (score > 0 ? `+${score}` : String(score))

const RestaurantRow = React.memo(
	({
		restaurant,
		selected,
		alarm,
		onSelect,
	}: {
		restaurant: RankedRestaurant
		selected: boolean
		// Der nächste offene Alarm des Restaurants
		alarm: RestaurantCommunication | undefined
		onSelect: (slug: string) => void
	}): JSX.Element => {
		const rowRef = React.useRef<HTMLDivElement>(null)
		const handleClick = React.useCallback(
			() => onSelect(restaurant.slug),
			[onSelect, restaurant.slug]
		)

		// Beim Blättern per Pfeiltaste wandert die Auswahl sonst aus dem sichtbaren Bereich.
		React.useEffect(() => {
			if (selected) rowRef.current?.scrollIntoView({ block: 'nearest' })
		}, [selected])

		return (
			<Box
				ref={rowRef}
				sx={
					restaurant.unprofitableEverywhere || (restaurant.relevanceScore ?? 0) < 0
						? selected
							? sxRowSelectedUnprofitable
							: sxRowUnprofitable
						: selected
						? sxRowSelected
						: sxRow
				}
				onClick={handleClick}
			>
				<Box flex="1" minWidth="0">
					<Txt variant="body2" noWrap title={restaurant.name}>
						{restaurant.name}
					</Txt>
					<Txt variant="caption" color="text.secondary" noWrap>
						{restaurant.addressCity ?? '—'} · {restaurant.settlements.length}{' '}
						{restaurant.settlements.length === 1 ? 'Ort' : 'Orte'}
					</Txt>
				</Box>
				{restaurant.isChain ? <ChainIcon /> : null}
				{alarm ? (
					<AlarmOutlined
						sx={sxRowAlarmIcon}
						titleAccess={`Alarm ${formatDateTime(alarm.dueAt as string)}`}
					/>
				) : null}
				{restaurant.relevanceScore === null ? null : (
					<Txt
						variant="caption"
						fontWeight="bold"
						color={restaurant.relevanceScore < 0 ? 'error.main' : 'success.main'}
						title="Relevanz"
					>
						{formatScore(restaurant.relevanceScore)}
					</Txt>
				)}
				<Txt
					variant="caption"
					color="text.secondary"
					width={MARGIN_COLUMN_WIDTH}
					textAlign="right"
					title={MARGIN_TITLE}
				>
					{formatMargin(restaurant.singleOrderMarginShare)}
				</Txt>
				<Txt variant="body2" width={REVENUE_COLUMN_WIDTH} textAlign="right">
					{formatEuro(restaurant.monthlyPlatformRevenue)}
				</Txt>
			</Box>
		)
	}
)
RestaurantRow.displayName = 'RestaurantRow'

export const RestaurantList = React.memo(
	({
		restaurants,
		selectedSlug,
		onSelect,
	}: {
		restaurants: RankedRestaurant[]
		selectedSlug: string | undefined
		onSelect: (slug: string) => void
	}): JSX.Element => {
		// Fehlende Strecken aller gelisteten Restaurants in ihre Orte einmal holen; die API speichert sie
		// und nimmt nur Paare, in die das Restaurant auch liefert.
		const slugs = React.useMemo(() => restaurants.map(({ slug }) => slug), [restaurants])
		const settlementIds = React.useMemo(
			() => [
				...new Set(
					restaurants.flatMap((restaurant) =>
						restaurant.settlements.map(({ settlementId }) => settlementId)
					)
				),
			],
			[restaurants]
		)
		useTours(slugs, settlementIds)
		const { communications } = useRestaurantCommunications()
		// Absteigend nach Termin, so bleibt je Restaurant der früheste Alarm übrig.
		const alarmBySlug = React.useMemo(
			() =>
				Object.fromEntries(
					communications
						.filter(({ channel }) => channel === 'alarm')
						.sort((a, b) => (b.dueAt as string).localeCompare(a.dueAt as string))
						.map((alarm) => [alarm.slug, alarm])
				),
			[communications]
		)
		const relevantCount = React.useMemo(
			() =>
				restaurants.filter(
					({ singleOrderMarginShare }) =>
						singleOrderMarginShare !== null && singleOrderMarginShare >= MIN_RELEVANT_MARGIN_SHARE
				).length,
			[restaurants]
		)

		return (
			<>
				<Box sx={sxHeaderRow}>
					<Txt variant="caption" color="text.secondary" flex="1">
						Restaurant
					</Txt>
					<Txt
						variant="caption"
						color="text.secondary"
						width={MARGIN_COLUMN_WIDTH}
						textAlign="right"
						title={MARGIN_TITLE}
					>
						DB
					</Txt>
					<Tooltip
						title={
							`${relevantCount} von ${restaurants.length} Restaurants mit DB ab ` +
							`${Math.round(MIN_RELEVANT_MARGIN_SHARE * 100)} %\n\n` +
							'Was Mitbestellt im Monat mit dem Restaurant verdient, über alle Orte. Sortiert nach ' +
							'DB × Umsatz des Restaurants.'
						}
						slotProps={slotPropsTooltip}
						arrow
					>
						<Box width={REVENUE_COLUMN_WIDTH}>
							<Txt
								variant="caption"
								color="text.secondary"
								textAlign="right"
								noWrap
								sx={sxTooltipHeader}
							>
								Mitbestellt
							</Txt>
						</Box>
					</Tooltip>
				</Box>
				{restaurants.map((restaurant) => (
					<RestaurantRow
						key={restaurant.slug}
						restaurant={restaurant}
						selected={restaurant.slug === selectedSlug}
						alarm={alarmBySlug[restaurant.slug]}
						onSelect={onSelect}
					/>
				))}
			</>
		)
	}
)
RestaurantList.displayName = 'RestaurantList'

// Die Karte steht in der rechten Spalte der Ansicht, die auch die Gemeinde-Karte darunter hält.
const sxCard = {
	flexShrink: 0,
	bgcolor: 'white',
	boxShadow: 2,
	borderRadius: '0.5rem',
	p: '1rem',
} as const

const sxDivider = { borderTop: '1px solid', borderColor: 'divider', m: '0.75rem 0' } as const

const sxTable = {
	display: 'grid',
	// minmax(0, …): sonst wächst die Ortsspalte mit dem längsten Namen, statt ihn zu kürzen.
	gridTemplateColumns: 'minmax(0, 1fr) auto auto',
	columnGap: '0.75rem',
	rowGap: '0.15rem',
	alignItems: 'baseline',
} as const

const sxTableRow = {
	'display': 'contents',
	'cursor': 'pointer',
	'&:hover > *': { bgcolor: 'grey.100' },
} as const

const sxTableRowUnprofitable = { ...sxTableRow, '& > *': { opacity: 0.4 } } as const

const SettlementPotentialRow = React.memo(
	({
		potential,
		selected,
		onSelectSettlement,
	}: {
		potential: RestaurantSettlementPotential
		selected: boolean
		onSelectSettlement: (settlementId: string) => void
	}): JSX.Element => {
		const handleClick = React.useCallback(
			() => onSelectSettlement(potential.settlementId),
			[onSelectSettlement, potential.settlementId]
		)

		return (
			<Box
				sx={isUnprofitable(potential.singleOrderMarginShare) ? sxTableRowUnprofitable : sxTableRow}
				onClick={handleClick}
				title={selected ? 'Gemeinde ausblenden' : 'Gemeinde anzeigen'}
			>
				<Txt variant="body2" noWrap fontWeight={selected ? 'bold' : undefined}>
					{potential.settlementName}
				</Txt>
				<Txt variant="caption" color="text.secondary" textAlign="right" noWrap>
					{potential.minOrderValue === null ? '—' : formatEuro(potential.minOrderValue)}
				</Txt>
				<Txt variant="caption" color="text.secondary" textAlign="right" noWrap>
					{formatMargin(potential.singleOrderMarginShare)}
				</Txt>
			</Box>
		)
	}
)
SettlementPotentialRow.displayName = 'SettlementPotentialRow'

const sxTitleRow = { display: 'flex', alignItems: 'center', gap: '0.5rem' } as const

// Öffnet die Brief-Mail: Gemeinden ankreuzen, Vorschau, Versand.
const RestaurantMailButton = React.memo(
	({ restaurant }: { restaurant: RankedRestaurant }): JSX.Element => {
		const handleClick = React.useCallback(
			() => renderRestaurantMessageDialog(restaurant, 'EMAIL'),
			[restaurant]
		)

		return <IconButton iconName="MailOutlined" toolTip="E-Mail" onClick={handleClick} />
	}
)
RestaurantMailButton.displayName = 'RestaurantMailButton'

// Öffnet den Brief per LetterXpress: Gemeinden ankreuzen, Vorschau, Versand.
const RestaurantLetterButton = React.memo(
	({ restaurant }: { restaurant: RankedRestaurant }): JSX.Element => {
		const handleClick = React.useCallback(
			() => renderRestaurantMessageDialog(restaurant, 'PAPER'),
			[restaurant]
		)

		return (
			<IconButton
				iconName="MarkunreadMailboxOutlined"
				toolTip="Brief per Post"
				onClick={handleClick}
			/>
		)
	}
)
RestaurantLetterButton.displayName = 'RestaurantLetterButton'

// Legt Restaurant und Firma aus der Erhebung an, wenn sich ein Vertrag anbahnt; danach geht es auf
// Wunsch gleich ins Onboarding. Ist es schon angelegt, springt der Knopf in seine Restaurantansicht.
const RestaurantCreateButton = React.memo(
	({ restaurant }: { restaurant: RankedRestaurant }): JSX.Element => {
		const navigate = useNavigate()
		const { setSelectedRestaurantId } = useView()
		const { data: currentUser } = useCurrentUser()
		const createdRestaurantId = currentUser?.adminRestaurants.find(
			({ slug }) => slug === restaurant.slug
		)?.id

		const handleOpen = React.useCallback(() => {
			if (createdRestaurantId) {
				setSelectedRestaurantId(createdRestaurantId)
				navigate(RESTAURANTS_PATH)
			}
		}, [createdRestaurantId, navigate, setSelectedRestaurantId])

		const handleCreate = React.useCallback(async () => {
			const result = await createRestaurantFromListing({ slug: restaurant.slug })
			if (result.success) {
				Dialog.render({
					title: result.created ? 'Restaurant angelegt' : 'Schon angelegt',
					description: result.created
						? `${restaurant.name} ist samt Firma mit Name, Anschrift und Kontakt angelegt. ` +
						  'Rechtsform, Inhaber, Register und Steuernummern kommen im Onboarding dazu.'
						: `${restaurant.name} ist schon als Restaurant angelegt.`,
					buttons: [
						{ id: 'close', label: 'Schließen' },
						{
							id: 'onboarding',
							label: 'Zum Onboarding',
							variant: 'contained',
							onClick: () => {
								setSelectedRestaurantId(result.restaurantId)
								navigate(RESTAURANTS_PATH)
							},
						},
					],
				})
			} else {
				handleMutationError('createRestaurantFromListing', result.errors)
			}
		}, [navigate, restaurant.name, restaurant.slug, setSelectedRestaurantId])

		const handleClick = React.useCallback(
			() =>
				Dialog.render({
					title: 'Restaurant anlegen?',
					description:
						`Restaurant und Firma für ${restaurant.name} aus der Erhebung anlegen, ` +
						'damit Onboarding und Vertrag starten können.',
					buttons: [
						{ id: 'cancel', label: 'Abbrechen' },
						{ id: 'create', label: 'Anlegen', variant: 'contained', onClick: handleCreate },
					],
				}),
			[handleCreate, restaurant.name]
		)

		// Ganz rechts in der Titelzeile, abgesetzt von Brief, Mail und Lieferando-Link.
		return createdRestaurantId ? (
			<IconButton
				iconName="StorefrontOutlined"
				toolTip="Zum Restaurant"
				onClick={handleOpen}
				m="0 0 0 auto"
			/>
		) : (
			<IconButton
				iconName="AddBusinessOutlined"
				toolTip="Restaurant anlegen"
				onClick={handleClick}
				m="0 0 0 auto"
			/>
		)
	}
)
RestaurantCreateButton.displayName = 'RestaurantCreateButton'

const RELEVANCE_SCORES = [-3, -2, -1, 0, 1, 2, 3]

const sxScoreRow = {
	display: 'flex',
	alignItems: 'center',
	gap: '0.5rem',
	m: '0.5rem 0 0 0',
} as const

const sxScoreButton = { p: '0 0.5rem', minWidth: '2rem' } as const

// Relevanz von -3 bis +3; ein Klick auf den gewählten Wert hebt sie auf.
const RestaurantRelevanceScore = React.memo(
	({ restaurant }: { restaurant: RankedRestaurant }): JSX.Element => {
		const handleChange = React.useCallback(
			async (_event: React.MouseEvent<HTMLElement>, relevanceScore: number | null) => {
				const result = await setRestaurantRelevanceScore({ slug: restaurant.slug, relevanceScore })
				if (!result.success) handleMutationError('setRestaurantRelevanceScore', result.errors)
			},
			[restaurant.slug]
		)

		return (
			<Box sx={sxScoreRow}>
				<Txt variant="caption" color="text.secondary">
					Relevanz
				</Txt>
				<ToggleButtonGroup
					size="small"
					exclusive
					value={restaurant.relevanceScore}
					onChange={handleChange}
				>
					{RELEVANCE_SCORES.map((score) => (
						<ToggleButton key={score} value={score} sx={sxScoreButton}>
							{formatScore(score)}
						</ToggleButton>
					))}
				</ToggleButtonGroup>
			</Box>
		)
	}
)
RestaurantRelevanceScore.displayName = 'RestaurantRelevanceScore'

// Die Infokarte eines Restaurants: sein Umsatzpotenzial je Ort; ein Klick zeigt die Gemeinde in
// einer eigenen Karte darunter.
export const RestaurantDetail = React.memo(
	({
		restaurant,
		selectedSettlementId,
		onSelectSettlement,
	}: {
		restaurant: RankedRestaurant
		selectedSettlementId: string | undefined
		onSelectSettlement: (settlementId: string) => void
	}): JSX.Element => (
		<Box sx={sxCard}>
			<Box sx={sxTitleRow}>
				<Txt variant="h6">{restaurant.name}</Txt>
				{restaurant.isChain ? <ChainIcon /> : null}
				<RestaurantLetterButton restaurant={restaurant} />
				<RestaurantMailButton restaurant={restaurant} />
				<LieferandoLinkButton
					url={getLieferandoRestaurantUrl(restaurant.slug)}
					toolTip="Bei Lieferando öffnen"
				/>
				<RestaurantCreateButton restaurant={restaurant} />
			</Box>
			<Txt variant="caption" color="text.secondary" display="block">
				{[
					restaurant.addressLine,
					[restaurant.addressZipCode, restaurant.addressCity].filter(Boolean).join(' '),
				]
					.filter(Boolean)
					.join(', ') || '—'}
				{restaurant.rating === null ? '' : ` · ${restaurant.rating.toFixed(1).replace('.', ',')} ★`}
				{restaurant.ratingCount === null
					? ''
					: ` (${(restaurant.ratingCount / 1000).toLocaleString('de-DE', {
							maximumFractionDigits: 1,
					  })}K)`}
			</Txt>
			{restaurant.cuisines?.length ? (
				<Txt variant="caption" color="text.secondary" display="block">
					{restaurant.cuisines.join(' · ')}
				</Txt>
			) : null}
			<RestaurantRelevanceScore restaurant={restaurant} />
			<RestaurantAdditionalData
				key={`contact-${restaurant.slug}`}
				slug={restaurant.slug}
				phone={restaurant.phone}
				email={restaurant.email}
			/>
			<Box sx={sxDivider} />
			<Box sx={sxTable}>
				<Txt variant="caption" color="text.secondary">
					Ort
				</Txt>
				<Txt variant="caption" color="text.secondary" textAlign="right">
					MBW
				</Txt>
				<Txt variant="caption" color="text.secondary" textAlign="right" title={MARGIN_TITLE}>
					DB
				</Txt>
				{restaurant.settlements.map((potential) => (
					<SettlementPotentialRow
						key={potential.settlementId}
						potential={potential}
						selected={potential.settlementId === selectedSettlementId}
						onSelectSettlement={onSelectSettlement}
					/>
				))}
			</Box>
			<RestaurantCommunicationLog key={`log-${restaurant.slug}`} slug={restaurant.slug} />
		</Box>
	)
)
RestaurantDetail.displayName = 'RestaurantDetail'
